import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import YouTube from 'youtube-iframe';
import Player from '@vimeo/player';

gsap.registerPlugin(ScrollTrigger);

export default class VideoHandler {
	customPlayer() {
		gsap.utils.toArray('[data-video="container"]').forEach((e) => {
			if (!e) return;

			const videoType = e.getAttribute("data-video-type");
			
			const video = e.querySelector('[data-video-url]');
			if (!video) return;

			const url = video.getAttribute("data-video-url");
			if (!url) return;
			
			const playButton = e.querySelector('[data-video-play]');
			const muteButton = e.querySelector('[data-video-mute]');
			const overlay = e.querySelector('[data-video-overlay]');
			const playType = e.getAttribute("data-video-play");
			const { type, id, hash } = getVideoTypeAndId(url);
			let player;

			if (type === "youtube") {
				loadYouTubePlayer(id);
			} else if (type === "vimeo") {
				loadVimeoPlayer(id, hash, url);
			} else if (type === "mp4") {
				loadMP4Video(url);
			}
			// }


			function getVideoTypeAndId(url) {
				const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]+)/;
				const vimeoRegex = /(?:vimeo\.com\/(?:video\/)?(?:channels\/\w+\/)?(?:groups\/\w+\/videos\/)?(?:album\/\w+\/video\/)?(?:showcase\/\w+\/video\/)?)([\d]+)(?:\/([a-zA-Z0-9]+))?/;
				const mp4Regex = /\.mp4(\?.*)?$/;

				let match;
				if ((match = url.match(youtubeRegex))) {
					return { type: "youtube", id: match[1] };
				} else if ((match = url.match(vimeoRegex))) {
					// For Vimeo, combine video ID and privacy hash if present
					const videoId = match[1];
					const privacyHash = match[2]; // This captures the hash like 'b50ca94c5c'
					return { 
						type: "vimeo", 
						id: videoId,
						hash: privacyHash || null
					};
				} else if (mp4Regex.test(url)) {
					return { type: "mp4", id: url };
				}
				return { type: "mp4", id: url };
			}

			function loadYouTubePlayer(videoId) {
				require('youtube-iframe').load(function(YT) {
					player = new YT.Player(video, {
						videoId: videoId,
						playerVars: {
							controls: videoType === 'full-width' ? '0' : 1,
							modestbranding: 1,
							rel: 0,
							showinfo: 0,
							iv_load_policy: 3,
							cc_load_policy: 0,
							fs: 0,
							autohide: 1      
						},
						events: {
							onReady: function (event) {	
								if (playType === "autoplay") {
									event.target.mute();
									setupScrollTrigger(event.target);
									if (videoType === 'full-width') {
										setupVideoControls(event.target, playType, videoType);
									}
								} else {
									if (playButton) {
										setupPlayButton(event.target);
									} else {
										setupVideoControls(event.target, playType, videoType);
									}
								}
								if (muteButton) {
									setupMuteButton(event.target);
								}

							},
							onStateChange: function(event) {
								if (event.data === YT.PlayerState.PLAYING) {
									if (videoType === 'inline') {
										const iframe = event.target.getIframe();
										if (iframe.classList.contains('pointer-events-none')) {
											iframe.classList.remove('pointer-events-none');
										}
									}
								}
							}
						},
					});
				});
			}

			function loadVimeoPlayer(videoId, privacyHash, originalUrl) {
				const playerOptions = {
					controls: false,
					muted: playType === "autoplay",
					loop: true,
				};
				
				// For private/unlisted videos with hash, use the full URL
				// Otherwise use the video ID
				if (privacyHash) {
					playerOptions.url = originalUrl;
				} else {
					playerOptions.id = videoId;
				}
				
				player = new Player(video, playerOptions);

				player.ready().then(() => {
					if (playType === "autoplay") {
						setupScrollTrigger(player);
						if (videoType === 'full-width') {
							setupVideoControls(player, playType, videoType);
						}
					} else {
						if (playButton) {
							setupPlayButton(player);
						} else {
							setupVideoControls(player, playType, videoType);
						}
					}
					if (muteButton) {
						setupMuteButton(player);
					}
				});
			}
			
			function loadMP4Video(videoUrl) {
				const videoElement = document.createElement("video");
				videoElement.src = videoUrl;
				videoElement.controls = false;
				videoElement.classList.add('w-full');
				videoElement.classList.add('h-full');
				videoElement.classList.add('object-cover');
				videoElement.muted = playType === "autoplay";
				videoElement.playsInline = true;
				videoElement.loop = true;

				video.innerHTML = "";
				video.appendChild(videoElement);

				if (playType === "autoplay") {
					setupScrollTrigger(videoElement);
					if (videoType === 'full-width') {
						setupVideoControls(videoElement, playType, videoType);
					}
				} else {
					if (playButton) {
						setupPlayButton(videoElement);
					} else {
						setupVideoControls(videoElement, playType, videoType);
					}
				}
				if (muteButton) {
					setupMuteButton(videoElement);
				}
			}

			function setupScrollTrigger(videoPlayer) {
				ScrollTrigger.create({
					trigger: e,
					start: "top 100%",
					end: "bottom 0%",
					toggleActions: "play pause resume pause",
					onEnter: () => playVideo(videoPlayer),
				});
				if (e.querySelector('[data-video-poster]')) {
					e.querySelector('[data-video-poster]').classList.add('hidden')
				};
			}
			
			function setupPlayButton(videoPlayer) {
				if (!playButton) return;
				
				const textSpan = playButton.querySelector('span:first-child');
				
				playButton.addEventListener("click", async () => {
					if (await isPlaying(videoPlayer)) {
						pauseVideo(videoPlayer);
						if (textSpan) textSpan.textContent = "Play video";
					} else {
						// Don't automatically unmute - respect the current mute state
						playVideo(videoPlayer);
						if (textSpan) textSpan.textContent = "Pause video";
						if (e.querySelector('[data-video-poster]')) {
							e.querySelector('[data-video-poster]').classList.add('hidden');
						}
					}
				});
			}
			
			function setupMuteButton(videoPlayer) {
				if (!muteButton) return;
				
				const textSpan = muteButton.querySelector('span:first-child');
				
				muteButton.addEventListener("click", async () => {
					if (await isMuted(videoPlayer)) {
						unmuteVideo(videoPlayer);
						if (textSpan) textSpan.textContent = "Mute";
					} else {
						muteVideo(videoPlayer);
						if (textSpan) textSpan.textContent = "Unmute";
					}
				});
			}

			function playVideo(videoPlayer) {
				if (videoPlayer.play) {
					if (type === "mp4") {
						videoPlayer.load();
					}
					videoPlayer.play();
				} else if (videoPlayer.playVideo) {
					videoPlayer.playVideo();
				}

				// Add playing state - controls will fade out on non-hover
				e.classList.add('video-playing');

				if (playButton) {
					playButton.classList.remove('opacity-100');
					playButton.classList.add('opacity-0');
				}
				if (muteButton) {
					muteButton.classList.remove('opacity-100');
					muteButton.classList.add('opacity-0');
				}
				if (overlay) {
					overlay.classList.remove('opacity-50');
					overlay.classList.add('opacity-0');
				}
			}
			
			function pauseVideo(videoPlayer) {
				if (videoPlayer.pause) {
					videoPlayer.pause();
				} else if (videoPlayer.pauseVideo) {
					videoPlayer.pauseVideo();
				}

				// Remove playing state - controls always visible when paused
				e.classList.remove('video-playing');

				if (playButton) {
					playButton.classList.remove('opacity-0');
					playButton.classList.add('opacity-100');
				}
				if (muteButton) {
					muteButton.classList.remove('opacity-0');
					muteButton.classList.add('opacity-100');
				}
				if (overlay) {
					overlay.classList.remove('opacity-0');
					overlay.classList.add('opacity-50');
				}
			}
			
			async function isPlaying(videoPlayer) {
				if (videoPlayer.getPlayerState) {
					return videoPlayer.getPlayerState() === 1;
				} else if (videoPlayer.getPaused) {
					try {
						const isPaused = await videoPlayer.getPaused();
						return !isPaused;
					} catch (e) {
						return false;
					}
				} else {
					return !videoPlayer.paused;
				}
			}
			
			function muteVideo(videoPlayer) {
				if (videoPlayer.mute) {
					videoPlayer.mute();
				} else if (videoPlayer.setVolume) {
					// Vimeo uses promises
					videoPlayer.setVolume(0).catch(() => {});
				} else {
					videoPlayer.muted = true;
				}
			}
			
			function unmuteVideo(videoPlayer) {
				if (videoPlayer.unMute) {
					videoPlayer.unMute();
				} else if (videoPlayer.setVolume) {
					// Vimeo uses promises
					videoPlayer.setVolume(1).catch(() => {});
				} else {
					videoPlayer.muted = false;
				}
			}
			
			async function isMuted(videoPlayer) {
				if (videoPlayer.isMuted) {
					return videoPlayer.isMuted();
				} else if (videoPlayer.getVolume) {
					try {
						const volume = await videoPlayer.getVolume();
						return volume === 0;
					} catch (e) {
						return true;
					}
				} else {
					return videoPlayer.muted;
				}
			}

		});
	}
}